/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useEffect, useMemo, useState, useTransition } from "react";

import { ViewModel } from "../../Common/ViewModel";
import { Panel, PanelNote } from "../../../../ui/components/Panel";
import { T } from "@adapptio/lib-components";
import { termsEditorViewEditor } from "../../../../terms/viewEditor";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import {
	EditorInspectorContext,
	IEditorInspectorContext
} from "../../../../ui/contexts/EditorInspectorContext";
import { TViewSelectionItemOrEvent } from "../../Common/ViewEditSelection";
import { InspectViewSelectionItem } from "../../Common/InspectViewSelectionItem";
import { REFERENCE_TYPE } from "../../../Core/ReferenceGuide/ReferenceGuideTypes";

function getSelectedItemOrEvent(model: ViewModel): TViewSelectionItemOrEvent {
	const selection = model.getSelection();
	const selectedItems = selection.getItems();
	const eventNodes = selection.getEventNodes();

	if (eventNodes.length === 1) {
		return eventNodes[0];
	}

	if (selectedItems.length === 0) {
		return null;
	}

	return selectedItems[0];
}

export interface IViewEditorInspectorProps {
	model: ViewModel;
}

export const ViewEditorInspector: React.FunctionComponent<IViewEditorInspectorProps> = ({ model }) => {
	const [ isPending, startTransition ] = useTransition();
	const editSelection = useMemo(() => model.getSelection(), [ model ]);

	const [ areMultipleItemsSelected, setAreMultipleItemsSelected ] = useState(
		() => editSelection.getItems().length > 1
	);
	const [ areMultipleEventNodesSelected, setAreMultipleEventNodesSelected ] = useState(
		() => editSelection.getEventNodes().length > 1
	);
	const [ selectedEventName, setSelectedEventName ] = useState(() => editSelection.getEventName());

	const [ selectedItemOrEvent, setSelectedItemOrEvent ] = useState<TViewSelectionItemOrEvent>(() =>
		getSelectedItemOrEvent(model)
	);

	const refreshSelectedItemOrEvent = useCallback(() => {
		const item = getSelectedItemOrEvent(model);

		setSelectedItemOrEvent(item ? { ...item } : item);
	}, [ model, setSelectedItemOrEvent ]);

	const getNodeById = useCallback(
		(nodeId: number) => model.getDesignContext().getNodeById(nodeId),
		[ model ]
	);

	const selectedModel = useMemo(
		() => getNodeById(selectedItemOrEvent?.nodeId) || null,
		[ selectedItemOrEvent, getNodeById ]
	);

	const inspectorCtx = useMemo<IEditorInspectorContext>(() => {
		return {
			readOnly: false,
			inputDebounceTimeout: 300,
			selectedEventName: selectedEventName,
			onEventSelect: (eventName) => {
				editSelection.selectEvent(selectedModel, eventName);
			},
			onEventUnselect: (eventName) => {
				editSelection.deselectEvent(eventName);
			},
			referenceItem: {
				category: REFERENCE_TYPE.COMPONETS,
				type: selectedModel?.schema?.name || null,
				name: REFERENCE_TYPE.COMPONETS
			}
		};
	}, [ editSelection, selectedEventName, selectedModel ]);

	useEffect(() => {
		const handleModelChange = () => refreshSelectedItemOrEvent();

		const handleSelectionChange = () => {
			setAreMultipleItemsSelected(editSelection.getItems().length > 1);
			startTransition(() => {
				refreshSelectedItemOrEvent();
			});
		};

		const handleEventChange = () => {
			setSelectedEventName(editSelection.getEventName());
		};

		const handleEventNodesChange = () => {
			setAreMultipleEventNodesSelected(editSelection.getEventNodes().length > 1);
			refreshSelectedItemOrEvent();
		};

		onEvent(model.onModelChange, handleModelChange);
		onEvent(editSelection.onChange, handleSelectionChange);
		onEvent(editSelection.onEventChange, handleEventChange);
		onEvent(editSelection.onEventNodesChange, handleEventNodesChange);

		return () => {
			offEvent(model.onModelChange, handleModelChange);
			offEvent(editSelection.onChange, handleSelectionChange);
			offEvent(editSelection.onEventChange, handleEventChange);
			offEvent(editSelection.onEventNodesChange, handleEventNodesChange);
		};
	}, [ model, editSelection, refreshSelectedItemOrEvent ]);

	return (
		<Panel scroll={true} mark="view-inspector" pending={isPending}>
			<EditorInspectorContext.Provider value={inspectorCtx}>
				<InspectViewSelectionItem selectionItem={selectedItemOrEvent} getNodeById={getNodeById} />

				{areMultipleEventNodesSelected ? (
					<PanelNote>
						<p>
							<T
								domain="editor"
								term={termsEditorViewEditor.inspector.cannotEditMultipleEventNodes}
							/>
						</p>
					</PanelNote>
				) : null}
				{areMultipleItemsSelected && !areMultipleEventNodesSelected ? (
					<PanelNote>
						<p>
							<T
								domain="editor"
								term={termsEditorViewEditor.inspector.cannotEditMultipleComponents}
							/>
						</p>
					</PanelNote>
				) : null}
				{!selectedModel && !isPending ? (
					<PanelNote>
						<p>
							<T
								domain="editor"
								term={termsEditorViewEditor.inspector.selectComponentToInspect}
							/>
						</p>
					</PanelNote>
				) : null}
			</EditorInspectorContext.Provider>
		</Panel>
	);
};
